import { h } from 'vue';
import {
  createRouter,
  createWebHistory,
  useRouter 
} from 'vue-router'

import Home from '@/components/Home';
import Management from '@/components/Management'
import Marketing from '@/components/Marketing';
import ProjectManagement from '@/components/ProjectManagement';
import QualityAssurance from '@/components/QualityAssurance';
import Field from '@/components/Field';
import ShopperCosts from '@/components/ShopperCosts';
import OrderManagement from '@/components/OrderManagement';
import InvoiceManagement from '@/components/InvoiceManagement';
import CostCenterManagement from '@/components/CostCenterManagement';
import Archive from '@/components/Archive';
import Blank from '@/components/Blank';
import AgencyCosts from '@/components/AgencyCosts';
import Invoicing from '@/components/Invoicing';
import UserManagement from '@/components/UserManagement';

import Imprint from '@/components/Imprint';
import DataProtection from '@/components/DataProtection';
import Page401 from '@/components/Page401';
import Page404 from '@/components/Page404';
import Page500 from '@/components/Page500';
import PageSignInFailed from '@/components/PageSignInFailed';
import Callback from '@/components/Callback';

import AppConfig from '@/mixins/AppConfig';

import AuthenticationService from '@/services/AuthenticationService';

const router = createRouter({
  history: createWebHistory(),
  mode: 'history',
  base: process.env.BASE_URL,
  linkActiveClass: 'active',

  routes: [
    {
      path: '/',
      name: 'Home',
      meta: { MessageKey: 'Webportal.Home', requiresAuth: true },
      redirect: '/_', // Fallback will be applied while checking permission
      component: Home,
      children: [
        {
          path: '/_',
          name: 'blank',
          component: Blank
        },
        {
          path: '/management',
          name: 'management',
          meta: {checkPermission: ['Management']},
          component: Management
        },
        {
          path: '/sales',
          name: 'sales',
          meta: {checkPermission: ['Sales']},
          component: Marketing
        },
        {
          path: '/projectManagement',
          name: 'projectManagement',
          meta: {checkPermission: ['ProjectManagement']},
          component: ProjectManagement
        },
        {
          path: '/qualityAssurance',
          name: 'qualityAssurance',
          meta: {checkPermission: ['QualityAssurance']},
          component: QualityAssurance
        },
        {
          path: '/field',
          name: 'field',
          meta: {checkPermission: ['Field']},
          component: Field
        },
        {
          path: '/shopperCosts',
          name: 'shopperCosts',
          meta: {checkPermission: ['ShopperCosts']},
          component: ShopperCosts
        },
        {
          path: '/orderManagement',
          name: 'orderManagement',
          meta: {checkPermission: ['OrderManagement']},
          component: OrderManagement
        },
        {
          path: '/agency-costs',
          name: 'agencyCosts',
          meta: {checkPermission: ['AgencyCostsAndInvoicing', 'AllocationAsProjectManager']},
          component: AgencyCosts
        },
        {
          path: '/invoicing',
          name: 'invoicing',
          meta: {checkPermission: ['AgencyCostsAndInvoicing', 'AllocationAsProjectManager']},
          component: Invoicing
        },
        {
          path: '/invoiceManagement',
          name: 'invoiceManagement',
          meta: {checkPermission: ['InvoiceManagement']},
          component: InvoiceManagement
        },
        {
          path: '/costCenterManagement',
          name: 'costCenterManagement',
          meta: {checkPermission: ['CostCenterManagement']},
          component: CostCenterManagement
        },
        {
          path: '/archive',
          name: 'archive',
          meta: {checkPermission: ['Archive']},
          component: Archive
        },
        {
          path: '/imprint',
          name: 'imprint',
          component: Imprint
        },
        {
          path: '/dataprotection',
          name: 'dataprotection',
          component: DataProtection
        },
        {
          path: '/userManagement',
          name: 'userManagement',
          meta: {checkPermission: ['UserManagement']},
          component: UserManagement
        },
        
      ]
    },
    {
      path: '/callback',
      component: {
        setup() {
          const router = useRouter();

          AuthenticationService.getUser()
            .then(() => {
              if (!AuthenticationService.isAuthenticated) {
                return AuthenticationService.callback();
              }
            })
            .then(() => {
              // Restart the application so startup-only theme logic runs again.
              location.replace(AuthenticationService.redirectUri || '/');
            })
            .catch((error) => {
              if (error === 'unauthorized') {
                setTimeout(() => {
                  AuthenticationService.logout();
                }, 5000);

                router.push({ name: 'unauthorized' });
              } else {
                router.push({ name: 'signinfailed' });
              }
            });

          return () => h(Callback);
        },
      },
    },

    // ... the default route, when none of the above matches:
    {
      path: '/:pathMatch(.*)*',
      name: 'notfound',
      component: Page404
    },

    {
      path: '/:pathMatch(.*)*',
      name: 'error',
      component: Page500
    },

    {
      path: '/:pathMatch(.*)*',
      name: 'unauthorized',
      component: Page401
    },

    {
      path: '/:pathMatch(.*)*',
      name: 'signinfailed',
      component: PageSignInFailed
    },
  ]
});

// Check for authentication before navigating to target
router.beforeEach((to, from, next) => {
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!AuthenticationService.isAuthenticated) {
      AuthenticationService.login().then(() => {
        if (AuthenticationService.isAuthenticated)
          next();
      }).catch((e) => {
        if (e === 'unauthorized') {
          // Logout user after 5 seconds
          setTimeout(() => { AuthenticationService.logout(); }, 5000);
          router.push({ name: 'unauthorized' });
        }
        else {
          router.push({ name: 'signinfailed' });
        }
      });
    }
    else {
      next();
    }
  }
  else
    next();
});

// Check for permissions
router.beforeEach((to, from, next) => {
  const requiredPermissions = to.meta?.checkPermission;
  const userPermissions =
    AuthenticationService?.localUserProfile?.Permissions ?? [];

  if (requiredPermissions?.length) {
    const hasPermission = requiredPermissions.some(permission =>
      userPermissions.includes(permission)
    );

    if (!hasPermission) {
      return next('/');
    }
  }

  next();
});

export default router;
